import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMinhasSolicitacoes, getUsuarioLogado } from '../../api';
import SideBarNavFaturamento from '../../componets/SideBarNavFaturamento';
import { DARK_THEME_PAINEL_EXECUTIVO, LIGHT_THEME_PAINEL_EXECUTIVO } from '../../componets/temas';

// ─── Helpers ────────────────────────────────────────────────────────────────
const normalizar = (s) => (s || '').toString().trim().toLowerCase();

const campo = (linha, nome) => {
  const alvo = normalizar(nome);
  const chave = Object.keys(linha).find((k) => normalizar(k) === alvo);
  return chave ? (linha[chave] || '').toString().trim() : '';
};

const parseCarimbo = (valor) => {
  // Formato esperado: dd/mm/yyyy hh:mm:ss
  const m = /^(\d{2})\/(\d{2})\/(\d{4})[ T](\d{2}):(\d{2})(?::(\d{2}))?/.exec(valor || '');
  if (!m) return 0;
  const [, dia, mes, ano, h, min, seg] = m;
  return new Date(`${ano}-${mes}-${dia}T${h}:${min}:${seg || '00'}`).getTime() || 0;
};

const CAMPOS_DETALHE = [
  ['Tipo de Cliente', 'tipo_cliente'],
  ['Setor empresa', 'setor'],
  ['Faturamento', 'faturamento'],
  ['Necessário emissão de boleto?', 'boleto'],
  ['E-mail(s) para envio da nota fiscal', 'emails_nf'],
  ['Contato (email)', 'contato'],
  ['Necessário CNDs?', 'cnds'],
  ['Dia/Mês/Ano de competência', 'competencia'],
  ['Data para faturamento', 'data_faturamento'],
  ['Documentos extras', 'documentos'],
  ['Observações Gerais', 'observacoes'],
];

// ─── Card de solicitação ──────────────────────────────────────────────────────
const CardSolicitacao = ({ linha, aberto, onToggle, C }) => {
  const razaoSocial = campo(linha, 'Razão Social') || '—';
  const projeto = campo(linha, 'Nome do projeto');
  const contrato = campo(linha, 'Nº do contrato');
  const valor = campo(linha, 'Valor (R$)');
  const gp = campo(linha, 'Gerente de Projetos');
  const prestador = campo(linha, 'Prestador de Serviços');
  const carimbo = campo(linha, 'Carimbo de data/hora');
  const cnpj = campo(linha, 'CNPJ (Tomador)');
  const descricao = campo(linha, 'Descrição');

  return (
    <div
      className="rounded-lg border shadow-sm overflow-hidden transition-all"
      style={{ background: C.card, borderColor: C.border }}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-4"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: C.brandMid + '22', color: C.brand }}>
              {gp || 'GP não informado'}
            </span>
            {prestador && (
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: C.surface, color: C.muted }}>
                {prestador}
              </span>
            )}
            <span className="text-xs" style={{ color: C.muted }}>{carimbo}</span>
          </div>
          <div className="font-semibold truncate" style={{ color: C.text }}>{razaoSocial}</div>
          <div className="text-sm truncate" style={{ color: C.muted }}>
            {[projeto, contrato].filter(Boolean).join(' · ') || descricao}
          </div>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-right">
            <div className="text-xs" style={{ color: C.muted }}>Valor</div>
            <div className="font-mono font-semibold" style={{ color: C.text }}>{valor || '—'}</div>
          </div>
          <svg
            className={`w-4 h-4 transition-transform flex-shrink-0 ${aberto ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke={C.muted} strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {aberto && (
        <div className="px-5 pb-5 pt-1 border-t" style={{ borderColor: C.border }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mt-4">
            <div>
              <div className="text-xs font-bold uppercase" style={{ color: C.muted }}>CNPJ (Tomador)</div>
              <div className="text-sm" style={{ color: C.text }}>{cnpj || '—'}</div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase" style={{ color: C.muted }}>Descrição</div>
              <div className="text-sm whitespace-pre-wrap" style={{ color: C.text }}>{descricao || '—'}</div>
            </div>
            {CAMPOS_DETALHE.map(([label, key]) => {
              const valorCampo = campo(linha, label);
              if (!valorCampo) return null;
              return (
                <div key={key}>
                  <div className="text-xs font-bold uppercase" style={{ color: C.muted }}>{label}</div>
                  <div className="text-sm whitespace-pre-wrap" style={{ color: C.text }}>{valorCampo}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Página ───────────────────────────────────────────────────────────────────
const MinhasSolicitacoes = () => {
  const navigate = useNavigate();
  const [isLight] = useState(true);
  const C = isLight ? LIGHT_THEME_PAINEL_EXECUTIVO : DARK_THEME_PAINEL_EXECUTIVO;

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [dados, setDados] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [busca, setBusca] = useState('');
  const [expandido, setExpandido] = useState(null);

  const usuario = getUsuarioLogado();

  useEffect(() => {
    const carregar = async () => {
      setLoading(true);
      setErro(null);
      try {
        const resp = await getMinhasSolicitacoes();
        setDados(resp.data || []);
        setIsAdmin(!!resp.is_admin);
      } catch (e) {
        setErro(e.message || 'Não foi possível carregar as solicitações.');
      } finally {
        setLoading(false);
      }
    };
    carregar();
  }, []);

  const listaOrdenada = useMemo(
    () => [...dados].sort((a, b) => parseCarimbo(campo(b, 'Carimbo de data/hora')) - parseCarimbo(campo(a, 'Carimbo de data/hora'))),
    [dados]
  );

  const listaFiltrada = useMemo(() => {
    const termo = normalizar(busca);
    if (!termo) return listaOrdenada;
    return listaOrdenada.filter((linha) => {
      const alvo = [
        'Razão Social', 'Nome do projeto', 'Nº do contrato',
        'Descrição', 'Gerente de Projetos', 'CNPJ (Tomador)',
      ].map((campoNome) => normalizar(campo(linha, campoNome))).join(' ');
      return alvo.includes(termo);
    });
  }, [listaOrdenada, busca]);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: C.bg, fontFamily: "'DM Sans','Segoe UI',sans-serif", color: C.text }}>
      <SideBarNavFaturamento isLight={isLight} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>

        <header className="bg-gradient-to-r from-purple-900 to-pink-800 text-white px-6 py-4 flex justify-between items-center shadow-lg mb-6">
          <div className="flex items-center space-x-2">
            <span className="text-2xl font-bold tracking-tighter">YOUX</span>
            <span className="text-xl font-light">GROUP</span>
          </div>
          <button
            onClick={() => navigate('/inicio')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', color: '#fff', fontSize: 13, fontWeight: 600 }}
          >
            Menu Principal
          </button>
        </header>

        <main className="max-w-4xl mx-auto px-4 w-full pb-10">
          <div className="rounded-lg border shadow-sm mb-6 overflow-hidden border-t-8" style={{ background: C.card, borderColor: C.border, borderTopColor: '#581c87' }}>
            <div className="p-8">
              <h1 className="text-3xl font-semibold mb-2" style={{ color: C.text }}>Minhas Solicitações</h1>
              <p className="text-sm" style={{ color: C.muted }}>
                {isAdmin
                  ? 'Como administrador, você está visualizando todas as solicitações de emissão de NFSe registradas.'
                  : `Solicitações de emissão de NFSe enviadas por ${usuario?.sub || 'você'}.`}
              </p>
            </div>
          </div>

          {!loading && !erro && dados.length > 0 && (
            <div className="mb-4 flex items-center gap-3">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar por cliente, projeto, contrato ou GP..."
                  className="w-full px-4 py-2 rounded-lg border text-sm outline-none"
                  style={{ background: C.card, borderColor: C.border, color: C.text }}
                />
              </div>
              <span className="text-xs font-medium whitespace-nowrap" style={{ color: C.muted }}>
                {listaFiltrada.length} de {dados.length}
              </span>
            </div>
          )}

          {loading && (
            <div className="text-center py-16" style={{ color: C.muted }}>Carregando solicitações...</div>
          )}

          {!loading && erro && (
            <div className="rounded-lg border p-6 text-center text-sm" style={{ background: C.card, borderColor: C.border, color: C.neg }}>
              {erro}
            </div>
          )}

          {!loading && !erro && dados.length === 0 && (
            <div className="rounded-lg border p-10 text-center" style={{ background: C.card, borderColor: C.border, color: C.muted }}>
              Nenhuma solicitação encontrada para o seu usuário.
            </div>
          )}

          {!loading && !erro && dados.length > 0 && listaFiltrada.length === 0 && (
            <div className="rounded-lg border p-10 text-center" style={{ background: C.card, borderColor: C.border, color: C.muted }}>
              Nenhuma solicitação corresponde à busca.
            </div>
          )}

          <div className="space-y-3">
            {listaFiltrada.map((linha, idx) => (
              <CardSolicitacao
                key={idx}
                linha={linha}
                aberto={expandido === idx}
                onToggle={() => setExpandido(expandido === idx ? null : idx)}
                C={C}
              />
            ))}
          </div>
        </main>
      </div>
    </div>
  );
};

export default MinhasSolicitacoes;
